// ABOUTME: Dialog for deleting family with soft/hard delete options and confirmation
// ABOUTME: Shows deletion summary after successful deletion

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert
} from '@mui/material';
import { DeleteFormState } from '../types';
import { FamilyDeletionSummary } from '../../../types/api';

interface DeleteFamilyDialogProps {
  open: boolean;
  familyName: string;
  form: DeleteFormState;
  deletionSummary: FamilyDeletionSummary | null;
  error: string | null;
  onClose: () => void;
  onCancel: () => void;
  onAcknowledge: () => void;
  onChange: (form: DeleteFormState) => void;
  onSubmit: () => Promise<void>;
}

const DeleteFamilyDialog: React.FC<DeleteFamilyDialogProps> = ({
  open,
  familyName,
  form,
  deletionSummary,
  error,
  onClose,
  onCancel,
  onAcknowledge,
  onChange,
  onSubmit
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete Family</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {deletionSummary ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Family deleted successfully!
            </Alert>
            <Typography variant="h6" gutterBottom>Deletion Summary</Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2">• Children deleted: {deletionSummary.childrenDeleted}</Typography>
              <Typography variant="body2">• Adults removed: {deletionSummary.adultsRemoved}</Typography>
              <Typography variant="body2">• Adults deleted: {deletionSummary.adultsDeleted}</Typography>
              <Typography variant="body2">• Goals deleted: {deletionSummary.goalsDeleted}</Typography>
              <Typography variant="body2">• Behavior logs deleted: {deletionSummary.behaviorLogsDeleted}</Typography>
              <Typography variant="body2">• Images deleted: {deletionSummary.imagesDeleted}</Typography>
              <Typography variant="body2">• Child access tokens deleted: {deletionSummary.childAccessTokensDeleted}</Typography>
              <Typography variant="body2">• Refresh tokens deleted: {deletionSummary.refreshTokensDeleted}</Typography>
              <Typography variant="body2">• Password reset tokens deleted: {deletionSummary.passwordResetTokensDeleted}</Typography>
              <Typography variant="body2">• Policy acknowledgments deleted: {deletionSummary.userPolicyAcknowledgmentsDeleted}</Typography>
              <Typography variant="body2">• OAuth codes deleted: {deletionSummary.oAuthCodesDeleted}</Typography>
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Are you sure you want to delete <strong>{familyName}</strong>? This action will affect all related data including children, behavior logs, goals, and family members.
            </Typography>

            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Delete Type:</Typography>
              <RadioGroup
                value={form.deleteType}
                onChange={(e) => onChange({ ...form, deleteType: e.target.value as 'soft' | 'hard' })}
              >
                <FormControlLabel
                  value="soft"
                  control={<Radio />}
                  label="Soft Delete (recommended) - Data is marked as deleted but can be recovered"
                />
                <FormControlLabel
                  value="hard"
                  control={<Radio />}
                  label="Hard Delete (permanent) - Data is permanently removed from the system"
                />
              </RadioGroup>
            </FormControl>

            <TextField
              label={`Type the family name to confirm: ${familyName}`}
              value={form.confirmationText}
              onChange={(e) => onChange({ ...form, confirmationText: e.target.value })}
              fullWidth
              required
              error={error?.includes('Confirmation text')}
              helperText="This action cannot be undone"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!deletionSummary ? (
          <>
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              onClick={onSubmit}
              color="error"
              variant="contained"
            >
              Delete Family
            </Button>
          </>
        ) : (
          <Button onClick={onAcknowledge} variant="contained">
            OK
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DeleteFamilyDialog;
